import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useRef,
} from 'react'

interface ScrollContextValue {
	isScrolling: boolean
	startScrolling: () => void
	stopScrolling: () => void
}

const ScrollContext = createContext<ScrollContextValue | null>(null)

interface ScrollProviderProps {
	children: ReactNode
}

export function ScrollProvider({ children }: Readonly<ScrollProviderProps>) {
	const scrollingRef = useRef(false)
	const timeoutRef = useRef<number | null>(null)

	const stopScrolling = useCallback(() => {
		scrollingRef.current = false
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}, [])

	const startScrolling = useCallback(() => {
		scrollingRef.current = true
		// Clear any existing timeout
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		// Fallback timeout in case scroll end detection fails
		timeoutRef.current = window.setTimeout(() => {
			scrollingRef.current = false
			timeoutRef.current = null
		}, 1500)
	}, [])

	const value = useMemo(
		() => ({
			get isScrolling() {
				return scrollingRef.current
			},
			startScrolling,
			stopScrolling,
		}),
		[startScrolling, stopScrolling],
	)

	return (
		<ScrollContext.Provider value={value}>{children}</ScrollContext.Provider>
	)
}

// biome-ignore lint/style/useComponentExportOnlyModules: hook is co-located with its provider
export function useScrollContext() {
	const context = useContext(ScrollContext)
	if (!context) {
		throw new Error('useScrollContext must be used within a ScrollProvider')
	}
	return context
}
